import { camelize, dasherize } from '@ember/string';

import type {
  JsonApiDocument,
  JsonApiResource,
  JsonApiResourceIdentifier,
} from './jsonapi-document.ts';
import { isCamelCased, isDasherized } from './text.ts';

/**
 * Base class for JSON:API normalizers.
 */
export abstract class Normalizer {
  /**
   * Normalizes a JSON:API document by recursively normalizing the resource identifiers
   * in the data, relationships, and included sections.
   * @param document - The JSON:API document to normalize.
   */
  normalizeDocument(document: JsonApiDocument): void {
    if (!document || typeof document !== 'object') {
      console.error('Normalizer: Not a JSON:API document');
      return;
    }

    // Normalize primary data
    if (document.data) {
      if (Array.isArray(document.data)) {
        document.data.forEach((item) => this.normalizeResource(item));
      } else if (typeof document.data === 'object' && document.data !== null) {
        this.normalizeResource(document.data);
      }
    } else {
      console.error('Normalizer: No data in document');
    }

    // Normalize included data
    if (document.included && Array.isArray(document.included)) {
      document.included.forEach((item) => this.normalizeResource(item));
    }
  }

  /**
   * Normalizes a JSON:API resource by normalizing the resource identifier
   * and recursively normalizing the resource identifiers in the relationships.
   * @param resource - The JSON:API resource to normalize.
   */
  private normalizeResource(resource: JsonApiResource): void {
    this.normalizeResourceIdentifier(resource);

    this.removeOtherProperties(resource, [
      'type',
      'id',
      'relationships',
      'attributes',
      'links',
      'meta',
    ]);

    if (
      typeof resource.relationships === 'object' &&
      resource.relationships !== null
    ) {
      Object.values(resource.relationships).forEach((relationship) => {
        // Strip links from the relationship object itself — we only use `data`
        // for relationship resolution. Without this, Warp-Drive picks up
        // `links.related` and tries to lazy-fetch via a URL that doesn't
        // route correctly through the app's proxy/normalizer stack.
        delete (relationship as Record<string, unknown>)['links'];

        if (Array.isArray(relationship.data)) {
          relationship.data.forEach((value) => {
            this.normalizeResourceIdentifier(value);
            this.removeOtherProperties(value, ['type', 'id']);
          });
        } else if (
          typeof relationship.data === 'object' &&
          relationship.data !== null
        ) {
          this.normalizeResourceIdentifier(relationship.data);
          this.removeOtherProperties(relationship.data, ['type', 'id']);
        }
      });
    }
  }

  /**
   * Normalizes a JSON:API resource identifier
   * @param resource - The JSON:API resource identifier to normalize.
   */
  protected abstract normalizeResourceIdentifier(
    resource: JsonApiResourceIdentifier
  ): void;

  /**
   * Removes other properties from a JSON:API resource identifier,
   * leaving only the type and id properties.
   * This is necessary because our JSON:API server does not support other properties.
   * and Warp Drive might add "lid" (or, in a built application a minified property name).
   * @param resource - The JSON:API resource identifier to remove other properties from.
   * @param keepKeys - The keys to keep in the resource.
   */
  protected removeOtherProperties(
    resource: JsonApiResourceIdentifier,
    keepKeys: string[]
  ): void {
    if (resource && typeof resource === 'object') {
      const keys = Object.keys(resource);
      keys.forEach((key) => {
        if (!keepKeys.includes(key)) {
          delete (resource as unknown as Record<string, unknown>)[key];
        }
      });

      if (resource.id === null) {
        delete (resource as unknown as Record<string, unknown>).id;
      }
    }
  }
}

/**
 * Normalizer that converts camelCase resource identifiers to dasherized.
 * This is used when parsing the response from the API because
 * the API returns camelCase resource identifiers and WarpDrive expects
 * dasherized resource identifiers.
 */
export class NormalizeCamelCaseToDasherize extends Normalizer {
  /**
   * Normalizes a JSON:API resource identifier by converting camelCase to dasherized.
   * @param resource - The JSON:API resource identifier to normalize.
   */
  normalizeResourceIdentifier(resource: JsonApiResourceIdentifier): void {
    if (resource.type && typeof resource.type === 'string') {
      // Only normalize types that are in camelCase format
      if (isCamelCased(resource.type)) {
        resource.type = dasherize(resource.type);
      }
    }
  }
}

/**
 * Normalizer that converts dasherized resource identifiers to camelCase.
 * This is used when parsing the body sent to the API because
 * WarpDrive uses dasherized resource identifiers and the API expects
 * camelCase resource identifiers.
 */
export class NormalizeDasherizeToCamelCase extends Normalizer {
  /**
   * Normalizes a JSON:API resource identifier by converting dasherized to camelCase.
   * @param resource - The JSON:API resource identifier to normalize.
   */
  normalizeResourceIdentifier(resource: JsonApiResourceIdentifier): void {
    if (resource.type && typeof resource.type === 'string') {
      if (isDasherized(resource.type)) {
        resource.type = camelize(resource.type);
      }
    }
  }
}
